import React, { useEffect, useRef, useState, useMemo, useCallback, useLayoutEffect } from "react";
import { Box, Flex, useTranslation, Option, SingleSearchSelector, Input, Button, TextArea, Icon } from "@familyzone/component-library";
import CardBasedPage from "../templates/CardBasedPage";
import type { InfoResponse } from "../../types/ApiResponses";
import { useDeviceOptionsStore } from "../../storez/DeviceOptionsStore";
import Api from "../../utils/Api";
import { StatusLabel } from "../label/StatusLabel";
import { v4 as uuid } from "uuid";

const DEFAULT_TIMEOUT = 30;

const timeoutOptions: Option[] = [
  { label: "5", value: "5" },
  { label: "10", value: "10" },
  { label: "15", value: "15" },
  { label: "20", value: "20" },
  { label: "30", value: "30" },
  { label: "Custom", value: "custom" },
];

const accessReason: Option[] = [
  { label: "Troubleshooting", value: "troubleshooting" },
  { label: "Maintenance", value: "maintenance" },
  { label: "Software Update", value: "software_update" },
  { label: "Security Audit", value: "security_audit" },
  { label: "Configuration Change", value: "configuration_change" },
  { label: "Other", value: "other" },
];

const MAX_COMMENT_CHARS = 500;

type RemoteSupportState = "loading" | "enabled" | "disabled" | "unsupported" | "error" | "unknown";

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;

const getRemoteSupportStateFromInfo = (data: InfoResponse): RemoteSupportState => {
  if (!isRecord(data)) return "unsupported";

  const di = data.device_information;
  if (!isRecord(di)) return "unsupported";

  const enabled = di["remote_support_enabled"];

  if (typeof enabled === "boolean") {
    return enabled ? "enabled" : "disabled";
  }

  return "unsupported";
};

const refreshRemoteSupportState = (devId: string): Promise<RemoteSupportState> =>
  new Promise((resolve) => {
    Api.get_for_device(
      "/info",
      devId,
      (result: InfoResponse) => resolve(getRemoteSupportStateFromInfo(result)),
      () => resolve("error")
    );
  });

type ConsoleLine = {
  id: number;
  input: string;
  output?: string;
};

type TerminalUiStatus = {
  label: string | null; // text shown next to prompt
  color: string; // color for label
  placeholder: string; // input placeholder
  inputAllowed: boolean; // can user type/execute
};

const PromptColumn = React.memo(function PromptColumn({ prompt, lineCount }: { prompt: string; lineCount: number }) {
  return (
    <Flex direction="column" alignItems="flex-end" flexShrink={0}>
      <Box as="span" color="#7CFC00" whiteSpace="nowrap">
        {prompt}
      </Box>

      {lineCount > 1 &&
        Array.from({ length: lineCount - 1 }).map((_, i) => (
          <Box key={i} as="span" color="#7CFC00" whiteSpace="nowrap">
            {">"}
          </Box>
        ))}
    </Flex>
  );
});

const ConsoleLineRow = React.memo(function ConsoleLineRow({ line, prompt }: { line: ConsoleLine; prompt: string }) {
  if (!line.input) {
    return (
      <Box mb="sp8" color="#cfcfcf" whiteSpace="pre-wrap">
        {line.output}
      </Box>
    );
  }

  const inputLines = line.input.split("\n");

  return (
    <Box mb="sp8">
      <Flex alignItems="flex-start" gap="sp8" style={{ minWidth: "max-content" }}>
        <PromptColumn prompt={prompt} lineCount={inputLines.length} />

        <Flex direction="column" minWidth={0} flex={1}>
          {inputLines.map((inputLine, index) => (
            <Box key={index} as="span" whiteSpace="pre-wrap" style={{ userSelect: "text" }}>
              {inputLine || "\u00A0"}
            </Box>
          ))}
        </Flex>
      </Flex>

      {line.output !== undefined && line.output !== "" ? (
        <Box whiteSpace="pre-wrap" mt="sp4" color="#cfcfcf" style={{ userSelect: "text" }}>
          {line.output}
        </Box>
      ) : null}
    </Box>
  );
});
type RemoteSupportRequest = {
  command: string;
  timeout: number;
  reason: string;
  comments: string;
  sessionid: string;
};

type RemoteSupportResponse = {
  response?: {
    exit_status?: number;
    output?: Array<{
      stream?: number;
      text?: string;
    }>;
  };
};

const formatRemoteSupportOutput = (res: RemoteSupportResponse): string => {
  const exitStatus = res?.response?.exit_status;
  const output = res?.response?.output ?? [];

  let result = `Exit Status: ${exitStatus ?? "unknown"}\n`;

  if (!Array.isArray(output) || output.length === 0) {
    result += "Output: (empty)";
    return result;
  }

  result += "Output:\n";

  output.forEach((item) => {
    let streamLabel = "unknown";

    if (item?.stream === 1) streamLabel = "Standard Output";
    else if (item?.stream === 2) streamLabel = "Standard Error";

    result += `Stream: ${streamLabel}\n`;
    result += `Text:\n${item?.text ?? ""}\n`;
  });

  return result.trim();
};

const RemoteSupportAccess: React.FC = () => {
  const { t } = useTranslation();

  // -------------------
  // Device selection
  // -------------------

  // Extract device options from store
  const [deviceId, isParent] = useDeviceOptionsStore((state) => [state.options?.deviceid, state.options?.is_parent] as const);

  // Local state for device options and selection
  const [deviceOptions, setDeviceOptions] = useState<Option[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<Option | null>(null);

  // Populate device options on mount and when deviceId/isParent changes
  useEffect(() => {
    const baseOptions: Option[] = deviceId ? [{ label: String(deviceId), value: String(deviceId) }] : [];

    if (!isParent) {
      setDeviceOptions(baseOptions);
      setSelectedDevice(baseOptions[0] ?? null);
      return;
    }

    Api.get("/managedevice/ajax/device/children", (data: { device_ids?: string[] }) => {
      const childOptions: Option[] = (data.device_ids ?? []).map((dev) => ({
        label: String(dev),
        value: String(dev),
      }));

      const merged = [...baseOptions, ...childOptions];

      setDeviceOptions(merged);

      setSelectedDevice((prev) => {
        if (prev && merged.some((o) => o.value === prev.value)) {
          return prev; // keep user selection
        }
        return null; // fallback
      });
    });
  }, [isParent, deviceId]);

  const effectiveDeviceId = isParent ? String(selectedDevice?.value ?? "") : String(deviceId ?? "");

  const isValidDeviceId = effectiveDeviceId.trim().length > 0;

  // ----------------------
  // Timeout configurations
  // ----------------------
  const defaultOption = timeoutOptions.find((o) => o.value === String(DEFAULT_TIMEOUT)) ?? null;
  const [timeoutOption, setTimeoutOption] = useState<Option | null>(defaultOption);

  const [customTimeout, setCustomTimeout] = useState("");

  const isCustomTimeout = timeoutOption?.value === "custom";

  const effectiveTimeout = useMemo(() => {
    const raw = timeoutOption?.value === "custom" ? customTimeout : timeoutOption?.value;

    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? Math.trunc(n) : DEFAULT_TIMEOUT;
  }, [timeoutOption?.value, customTimeout]);

  const handleChangeTimeout = (selectedOption: Option | null) => {
    setTimeoutOption(selectedOption);
    if (selectedOption?.value !== "custom") setCustomTimeout("");
  };

  const handleCustomTimeoutChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (/^\d*$/.test(raw)) setCustomTimeout(raw); // allows "" or digits only
  };

  // -------------------
  // Reason for access
  // -------------------
  const [accessReasonOption, setAccessReasonOption] = useState<Option | null>(null);

  // --------------------
  // Additional comments
  // --------------------

  const [additionalComments, setAdditionalComments] = useState("");

  const isCommentsValid = !!accessReasonOption && additionalComments.trim().length > 0;

  const handleCommentsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setAdditionalComments(e.target.value.slice(0, MAX_COMMENT_CHARS));
  };

  // ---------------------------
  // Remote Support Config state
  // ---------------------------
  const [remoteSupportState, setRemoteSupportState] = useState<RemoteSupportState>("unknown");
  const [isConnected, setIsConnected] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [connectionUuid, setConnectionUUID] = useState("");

  const handleConnect = () => {
    setRemoteSupportState("unknown");
    setIsConnected(false);
    setConnectError(null);

    // Mandatory checks
    const devId = String(effectiveDeviceId ?? "").trim();
    if (!devId) {
      setConnectError(t("Please select a device."));
      return;
    }

    if (!isCommentsValid) {
      setConnectError(t("Please provide valid comments and reason for access."));
      return;
    }

    // We're opening a new "connection" so set UUID
    setConnectionUUID(uuid());

    setRemoteSupportState("loading");

    Api.get_for_device(
      "/info",
      devId,
      (result: InfoResponse) => {
        const state = getRemoteSupportStateFromInfo(result);
        setRemoteSupportState(state);

        if (state === "enabled") {
          setIsConnected(true);
          return;
        }

        if (state === "disabled") {
          setConnectError(t("Remote support access is disabled on this device."));
        } else if (state === "unsupported") {
          setConnectError(t("Remote support access is not supported on this device."));
        } else {
          setConnectError(t("Unable to connect."));
        }
      },
      () => {
        setRemoteSupportState("error");
        setConnectError(t("Unable to determine remote support status"));
      }
    );
  };

  const handleDisconnect = () => {
    // Cancel any running command (UI only)
    abortRef.current?.abort();
    abortRef.current = null;

    // Stop execution state
    setIsExecuting(false);

    // Reset connection flags
    setIsConnected(false);
    setRemoteSupportState("unknown");
    setConnectError(null);
    setPendingLineId(null);
  };

  // ---------------------------
  // Console state and logic
  // ---------------------------
  const shellRef = useRef<HTMLDivElement>(null); // Enables auto-scrolling and selection management
  const terminalInputRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null); // Manages command cancellation

  const [consoleLines, setConsoleLines] = useState<ConsoleLine[]>([]);
  const idToIndexRef = useRef(new Map<number, number>());
  const [inputHistory, setInputHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const draftRef = useRef("");
  const commandIdRef = useRef(0);
  const [commandInput, setCommandInput] = useState(""); // the actual input value bound to the terminal input
  const [isExecuting, setIsExecuting] = useState(false);
  const canConnect = Boolean(effectiveDeviceId) && isCommentsValid && !isConnected && remoteSupportState !== "loading";

  const basePrompt = `root@${effectiveDeviceId || "no-device"}:bash#`;

  const [pendingLineId, setPendingLineId] = useState<number | null>(null);

  const terminalUi: TerminalUiStatus = (() => {
    const disconnected = {
      label: "DISCONNECTED",
      color: "#FF3B3B",
      placeholder: "Click Connect to enable the console.",
      inputAllowed: false,
    };

    switch (remoteSupportState) {
      case "loading":
        return { label: "CHECKING", color: "#FFD700", placeholder: "Checking remote support status…", inputAllowed: false };
      case "disabled":
        return { label: "DISABLED", color: "#FFA500", placeholder: "Remote support is disabled on this device.", inputAllowed: false };
      case "unsupported":
        return {
          label: "NOT SUPPORTED",
          color: "#FF6B6B",
          placeholder: "Remote commands are not supported on this device.",
          inputAllowed: false,
        };
      case "error":
        return { label: "ERROR", color: "#FF3B3B", placeholder: "Unable to determine remote support status.", inputAllowed: false };
      case "enabled":
        return isConnected ? { label: "CONNECTED", color: "#7CFC00", placeholder: "", inputAllowed: true } : disconnected;
      default:
        return disconnected;
    }
  })();

  // Terminal should be locked while the latest command hasn't produced output yet.
  const isWaitingForOutput = pendingLineId !== null;

  const terminalLocked = isExecuting || isWaitingForOutput || !terminalUi.inputAllowed;

  const MAX_COMMANDS = 50; // cap console history to prevent memory bloat; adjust as needed

  const rebuildIndexMap = useCallback((lines: ConsoleLine[]) => {
    idToIndexRef.current = new Map(lines.map((l, i) => [l.id, i]));
  }, []);

  const appendCommand = (input: string) => {
    const id = ++commandIdRef.current;

    setConsoleLines((prev) => {
      const next = [...prev, { id, input }];

      if (next.length <= MAX_COMMANDS) {
        // update map incrementally
        idToIndexRef.current.set(id, next.length - 1);
        return next;
      }

      // trim oldest lines
      const sliced = next.slice(next.length - MAX_COMMANDS);
      rebuildIndexMap(sliced);
      return sliced;
    });

    setPendingLineId(id);

    if (input.trim()) {
      setInputHistory((prev) => [...prev, input]);
    }

    return id;
  };

  // Update the output for a specific command line by ID
  const setCommandOutput = (id: number, output: string) => {
    setConsoleLines((prev) => {
      const idx = idToIndexRef.current.get(id);
      if (idx == null) return prev;

      const line = prev[idx];
      if (!line || line.id !== id) return prev;

      const next = prev.slice();
      next[idx] = { ...next[idx], output };
      return next;
    });

    // if this output completes the pending line, clear waiting flag
    setPendingLineId((prev) => (prev === id ? null : prev));
  };

  // Prevent concurrent command execution which could cause race conditions with pendingLineId and consoleLines updates
  const inFlightRef = useRef(false);

  const executeCommand = async (command: string) => {
    const normalized = command.replace(/\s+$/, "");
    if (!normalized.trim()) return;

    if (inFlightRef.current) return;
    inFlightRef.current = true;

    const dev = effectiveDeviceId || "";

    // Always echo the command to the transcript like bash does
    const lineId = appendCommand(normalized);

    // reset browsing state like bash after Enter
    setHistoryIndex(-1);
    draftRef.current = "";

    const fail = (msg: string) => {
      setCommandOutput(lineId, msg); // also clears pendingLineId (because id matches pending)
      setCommandInput("");
      setIsExecuting(false);
      inFlightRef.current = false;
    };

    // now do your checks, but put messages in output instead of history
    if (!isCommentsValid) {
      fail("Please provide valid comments and reason for access.");
      return;
    }

    if (!dev) {
      fail("No device selected");
      return;
    }

    if (!isConnected) {
      fail("Click Connect to enable the console.");
      return;
    }

    // ... your existing remote-support recheck ...
    const latestState = await refreshRemoteSupportState(dev);
    setRemoteSupportState(latestState);
    if (latestState !== "enabled") {
      setIsConnected(false);
      fail(latestState === "disabled" ? "Remote support was disabled while you were connected." : "Remote support is not available.");
      return;
    }

    setIsExecuting(true);

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      // Execute the command via API call to the appliance
      await new Promise<void>((resolve, reject) => {
        const requestPayload: RemoteSupportRequest = {
          command: normalized,
          timeout: effectiveTimeout,
          reason: String(accessReasonOption?.value ?? ""),
          comments: additionalComments.trim(),
          sessionid: connectionUuid,
        };

        Api.post_for_device(
          "/ajax/diags/remote_support",
          requestPayload,
          dev,
          (result: RemoteSupportResponse) => {
            const formattedOutput = formatRemoteSupportOutput(result);

            setCommandOutput(lineId, formattedOutput || "Command executed successfully.");
            resolve();
          },
          () => {
            setCommandOutput(lineId, "Unable to run the command");
            reject();
          }
        );

        controller.signal.addEventListener(
          "abort",
          () => {
            reject(new DOMException("Aborted", "AbortError"));
          },
          { once: true }
        );
      });
    } catch (e) {
      if (controller.signal.aborted) return;
      setCommandOutput(lineId, "Command failed.");
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      setIsExecuting(false);
      setCommandInput("");
      inFlightRef.current = false;
    }
  };

  const moveCaretToEnd = () => {
    const ta = terminalInputRef.current;
    if (!ta) return;

    const len = ta.value.length;
    ta.setSelectionRange(len, len);
  };

  // Handle key events in the terminal input for command execution, history navigation, and keyboard shortcuts
  const handleShellKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (terminalLocked) return;

    if (e.key === "Enter" && !e.shiftKey) {
      if (isExecuting) return;
      if (!terminalUi.inputAllowed) return;
      e.preventDefault();

      const cmd = commandInput; // capture what user typed
      setCommandInput(""); // clear immediately so draft line doesn't "echo"
      draftRef.current = ""; // clear draft since we're executing
      setHistoryIndex(-1);

      void executeCommand(cmd);
      return;
    }

    const ta = e.currentTarget;
    const caret = ta.selectionStart ?? 0;
    const caretEnd = ta.selectionEnd ?? 0;
    const isSelection = caret !== caretEnd;

    const val = commandInput;
    const beforeCaret = val.slice(0, caret);
    const afterCaret = val.slice(caret);
    const isOnFirstLine = !beforeCaret.includes("\n");
    const isOnLastLine = !afterCaret.includes("\n");

    // History Up: preserve draft when starting browsing
    if (e.key === "ArrowUp") {
      if (!isOnFirstLine || isSelection) return;
      if (isExecuting) return;
      if (inputHistory.length === 0) return;
      if (historyIndex === inputHistory.length - 1) return;
      e.preventDefault();

      if (historyIndex === -1) draftRef.current = commandInput;

      const newIndex = historyIndex + 1;

      setHistoryIndex(newIndex);
      setCommandInput(inputHistory[inputHistory.length - 1 - newIndex]);
      requestAnimationFrame(() => {
        moveCaretToEnd();
        const el = shellRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      });

      return;
    }

    // History Down: restore draft when exiting browsing
    if (e.key === "ArrowDown") {
      if (!isOnLastLine || isSelection) return;
      if (isExecuting) return;
      if (inputHistory.length === 0) return;
      if (historyIndex === -1) return;
      e.preventDefault();

      const newIndex = historyIndex - 1;

      if (newIndex >= 0) {
        setHistoryIndex(newIndex);
        setCommandInput(inputHistory[inputHistory.length - 1 - newIndex]);
      } else {
        setHistoryIndex(-1);
        setCommandInput(draftRef.current);
        draftRef.current = "";
      }

      requestAnimationFrame(() => {
        moveCaretToEnd();
        const el = shellRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      });
      return;
    }
  };

  // Auto-scroll to bottom on new command or output
  useLayoutEffect(() => {
    const el = shellRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [consoleLines, commandInput]);

  // Auto-resize console height based on content
  useLayoutEffect(() => {
    const ta = terminalInputRef.current;
    if (!ta) return;

    ta.style.height = "auto";
    ta.style.height = `${ta.scrollHeight}px`;
  }, [commandInput]);

  useEffect(() => {
    if (!terminalLocked) {
      terminalInputRef.current?.focus();
    }
  }, [terminalLocked]);

  // Cleanup on unmount: abort any running command and reset state
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
      inFlightRef.current = false;
    };
  }, []);

  // Reset connection whenever device changes
  useEffect(() => {
    handleDisconnect();

    // Clear console + history when device switches
    setConsoleLines([]);
    commandIdRef.current = 0;
    idToIndexRef.current = new Map();
    setInputHistory([]);
    setCommandInput("");
    draftRef.current = "";
    setHistoryIndex(-1);
  }, [effectiveDeviceId]);

  return (
    <CardBasedPage
      title={t("Remote Support Access")}
      breadcrumbs={[
        { title: t("Debugging"), url: "/managedevice", isActive: false },
        { title: t("Diagnostics"), url: "/managedevice/settings/diagnostics", isActive: false },
        { title: t("Remote Support Access"), isActive: true },
      ]}
    >
      <Flex direction="column" gap="sp24" p="sp16">
        <Box borderRadius="sm" border="1px solid lightgrey" p="sp16">
          <Flex direction="column" gap="sp16" alignItems="stretch" fontSize="md">
            <Box fontWeight="bold">{t("Target Server")}</Box>

            {isParent && (
              <Flex direction="column" gap="sp16">
                <Box opacity={0.8}>{t("Select the appliance to connect and send commands to.")}</Box>

                <SingleSearchSelector
                  id="device-selector"
                  name="device-selector"
                  aria-label={t(`Selected device is ${effectiveDeviceId || ""}`)}
                  options={deviceOptions}
                  selected={selectedDevice}
                  placeholder={t("Select a device")}
                  onChange={(opt) => setSelectedDevice(opt)}
                  filterOptions={false}
                  showClearIcon={true}
                  disabled={deviceOptions.length === 0 || isExecuting || isWaitingForOutput}
                />
              </Flex>
            )}

            {!isParent && <Box>{deviceId}</Box>}

            <Flex gap="sp24" alignItems="flex-start" flexWrap="wrap">
              {/* Command Timeout */}
              <Flex flex="1" minW={0} direction="column" gap="sp16">
                <Box fontWeight="bold">{t("Command Timeout")}</Box>

                <SingleSearchSelector
                  id="timeout-selector"
                  name="timeout-selector"
                  aria-label={t(`Effective timeout is ${effectiveTimeout} seconds`)}
                  options={timeoutOptions}
                  selected={timeoutOption}
                  placeholder={t("Select duration")}
                  onChange={handleChangeTimeout}
                  filterOptions={false}
                  showClearIcon={false}
                />

                {isCustomTimeout && (
                  <Input
                    autoFocus
                    type="text"
                    inputMode="numeric"
                    placeholder={t("Enter timeout (seconds)")}
                    value={customTimeout}
                    onChange={handleCustomTimeoutChange}
                  />
                )}

                <Box opacity={0.8}>
                  {t("Commands will automatically time out after")} {effectiveTimeout}s
                </Box>
              </Flex>

              {/* Reason for Access */}
              <Flex flex="1" minW={0} direction="column" gap="sp16">
                <Box fontWeight="bold">
                  {t("Reason for Access")}{" "}
                  <Box as="span" color="#FF6B6B">
                    *
                  </Box>
                </Box>

                <SingleSearchSelector
                  id="reason-selector"
                  name="reason-selector"
                  aria-label={t(`Selected reason for access is ${accessReasonOption?.label || ""}`)}
                  options={accessReason}
                  selected={accessReasonOption}
                  placeholder={t("Select a reason for accessing the appliance")}
                  onChange={setAccessReasonOption}
                  filterOptions={false}
                  showClearIcon={true}
                  disabled={isConnected}
                />
              </Flex>
            </Flex>

            <Box fontWeight="bold">
              {t("Additional Comments")}{" "}
              <Box as="span" color="#FF6B6B">
                *
              </Box>
            </Box>

            <TextArea
              value={additionalComments}
              onChange={handleCommentsChange}
              placeholder={t("Provide additional details for auditing purposes")}
              rows={4}
              isDisabled={isConnected}
            />

            <Box fontSize="sm" opacity={0.8}>
              {additionalComments.length}/{MAX_COMMENT_CHARS}
            </Box>

            <Box opacity={0.8}>{t("Please be aware that comments can be viewed by the customer.")}</Box>

            {!isValidDeviceId && <Box color="#FF6B6B">{t("Please select a device")}</Box>}
            {!accessReasonOption && <Box color="#FF6B6B">{t("Please select the reason for access")}</Box>}
            {additionalComments.trim().length === 0 && (
              <Box color="#FF6B6B">{t("Please provide additional details for auditing purposes")}</Box>
            )}
          </Flex>

          <Flex mt="sp12" gap="sp12" alignItems="center">
            {!isConnected ? (
              <Button variant="primary" disabled={!canConnect} onClick={handleConnect}>
                {remoteSupportState === "loading" ? t("Connecting…") : t("Connect")}
              </Button>
            ) : (
              <Button variant="danger" onClick={handleDisconnect} disabled={terminalLocked}>
                {t("Disconnect")}
              </Button>
            )}

            {connectError && (
              <StatusLabel variant="error" minWidth="fit-content">
                {connectError}
              </StatusLabel>
            )}
          </Flex>
        </Box>

        <Box borderRadius="sm" border="1px solid lightgrey" p="sp16" fontSize="lg">
          <Flex direction="column" gap="sp12">
            <Box fontWeight="bold">{t("Console")}</Box>

            <Box fontSize="md" opacity={0.8}>
              {t("Run commands on the target server. Each command runs in isolation and does not preserve session state.")}
            </Box>

            <Flex alignItems="center" gap="sp12">
              <Flex
                p="sp12"
                minWidth="fit-content"
                borderRadius="sm"
                backgroundColor="neutrals.100"
                color="white"
                fontSize="md"
                fontWeight="bold"
                gap="sp12"
                alignItems="center"
              >
                <Icon icon="fa-screen-users" boxSize="12px" variant="solid" />
                {effectiveDeviceId || t("No Device")}
              </Flex>

              {isConnected ? (
                <StatusLabel variant="success" minWidth="fit-content">
                  {t("Connected")}
                </StatusLabel>
              ) : (
                <StatusLabel variant="error" minWidth="fit-content">
                  {t("Not Connected")}
                </StatusLabel>
              )}
            </Flex>
          </Flex>

          <Box
            ref={shellRef}
            role="log"
            aria-live="polite"
            tabIndex={0}
            mt="sp12"
            maxH="80vh"
            overflowX="auto"
            overflowY="auto"
            p="sp16"
            borderRadius="8px"
            border="1px solid #333"
            background="#000"
            color="#e6e6e6"
            fontFamily="monospace"
            fontSize="sm"
            style={{
              userSelect: "text",
              WebkitUserSelect: "text",
              cursor: !terminalUi.inputAllowed ? "not-allowed" : "text",
            }}
          >
            {consoleLines.map((line) => (
              <ConsoleLineRow key={line.id} line={line} prompt={basePrompt} />
            ))}
            <Flex alignItems="flex-start" gap="sp8" style={{ minWidth: "max-content" }}>
              {!terminalLocked && <PromptColumn prompt={basePrompt} lineCount={commandInput.split("\n").length} />}
              {!isConnected && terminalUi.label && (
                <Box as="span" fontWeight="bold" color={terminalUi.color} whiteSpace="nowrap">
                  [{terminalUi.label}]
                </Box>
              )}
              <textarea
                ref={terminalInputRef}
                aria-label={t("Terminal command input")}
                value={commandInput}
                onChange={(e) => {
                  const value = e.target.value;
                  setCommandInput(value);
                  draftRef.current = value;
                  setHistoryIndex(-1);
                }}
                onKeyDown={handleShellKeyDown}
                readOnly={terminalLocked}
                placeholder={!terminalUi.inputAllowed ? terminalUi.placeholder : ""}
                autoComplete="off"
                spellCheck={false}
                rows={1}
                wrap="off"
                style={{
                  flex: 1,
                  background: "transparent",
                  color: "#BCCCDC",
                  border: "none",
                  outline: "none",
                  fontFamily: "monospace",
                  fontSize: "inherit",
                  lineHeight: "inherit",
                  padding: 0,
                  margin: 0,
                  opacity: 1,
                  resize: "none",
                  whiteSpace: "pre",
                  wordBreak: "normal",
                  overflow: "hidden",
                  overflowWrap: "normal",
                  cursor: !terminalUi.inputAllowed ? "not-allowed" : "text",
                }}
              />
            </Flex>
          </Box>

          <Flex mt="sp8" gap="sp8">
            <Button
              variant="ghost"
              onClick={() => {
                setConsoleLines([]);
                commandIdRef.current = 0;
                idToIndexRef.current = new Map();
                setInputHistory([]);
                setCommandInput("");
                draftRef.current = "";
                setHistoryIndex(-1);
                setPendingLineId(null);
              }}
              disabled={terminalLocked || consoleLines.length === 0}
            >
              {t("Clear")}
            </Button>
          </Flex>
        </Box>
      </Flex>
    </CardBasedPage>
  );
};

export default RemoteSupportAccess;
